import * as React from 'react';
import { Line } from 'react-chartjs-2';
import { ChartOptions, ChartData } from 'chart.js';
import { Container } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Rep } from '../../../classes/Workout';
import { State } from '../../../reducers';

interface LiveGraphProps {
    currentRep: Rep;
    activityStatus: string;
    chartDataArray?: { x: number; y: number }[];
    chartMaxTime: number;
    elapsedTime?: number;
    dispatch: Dispatch<any>;
}

interface LiveGraphState {
    redirect: boolean;
    color: string;
    maxValue: number;
    chartData: any;
}
const mapStateToProps = (state: State) => ({
  currentRep: state.activities.currentRep,
  activityStatus: state.activities.activityStatus,
  chartDataArray: state.activities.chartData,
  chartMaxTime: state.activities.chartMaxTime,
  elapsedTime: state.activities.elapsedTime,
});
class LiveGraph extends React.PureComponent<LiveGraphProps, LiveGraphState> {
  constructor(props: LiveGraphProps) {
    super(props);
    this.generateChartData = this.generateChartData.bind(this);
  }

  private generateChartData(xMin: number, xMax: number): ChartData<'line'> {
    const dataArray = this.props.chartDataArray || [];
    const expectedStrength = this.props.currentRep?.expectedStrength || 0;
    const elapsedTime = this.props.elapsedTime || 0;
    const activityStatus = this.props.activityStatus;

    // Pass all points sorted — axis scale clips rendering; no data is removed from the array.
    // This lets old data visually roll off the left edge as xMin advances.
    const windowedData = [...dataArray].sort((a, b) => a.x - b.x);

    const latestDataPoint = dataArray.length > 0 ? dataArray[dataArray.length - 1] : { x: elapsedTime, y: 0 };

    // Suppress target zone during wait_hang — the user hasn't started pulling yet
    const showTarget = expectedStrength > 0 && activityStatus !== 'wait hang';
    const upper = expectedStrength * 1.2;
    const targetData = showTarget
      ? [{ x: xMin, y: expectedStrength }, { x: xMax, y: expectedStrength }]
      : [];
    const upperData = showTarget
      ? [{ x: xMin, y: upper }, { x: xMax, y: upper }]
      : [];

    return {
      datasets: [{
        label: 'currentDat',
        fill: false,
        showLine: false,
        pointBorderColor: '#F97316',
        pointBackgroundColor: '#F8FAFC',
        pointBorderWidth: 3,
        pointRadius: 6,
        borderColor: '#F97316',
        data: latestDataPoint.y > 0 ? [{ x: latestDataPoint.x, y: latestDataPoint.y }] : [],
        tension: 0,
      }, {
        // Lower target boundary line
        label: 'targetLower',
        fill: false,
        showLine: showTarget,
        pointRadius: 0,
        pointHitRadius: 0,
        borderColor: 'rgba(34, 197, 94, 0.7)',
        borderWidth: 1.5,
        borderDash: [6, 3],
        data: targetData,
        tension: 0,
      }, {
        // Upper target boundary — fills back to lower line
        label: 'targetUpper',
        fill: '-1',
        showLine: showTarget,
        pointRadius: 0,
        pointHitRadius: 0,
        borderColor: 'rgba(34, 197, 94, 0.7)',
        borderWidth: 1.5,
        borderDash: [6, 3],
        backgroundColor: 'rgba(34, 197, 94, 0.12)',
        data: upperData,
        tension: 0,
      }, {
        label: 'Effort',
        fill: true,
        showLine: true,
        backgroundColor: 'rgba(249, 115, 22, 0.2)',
        borderWidth: 2,
        pointRadius: 0,
        borderColor: '#F97316',
        data: windowedData,
        tension: 0.4,
      }],
    };
  }

  private generateChartOptions(xMin: number, xMax: number): ChartOptions<'line'> {
    const expectedStrength = this.props.currentRep?.expectedStrength || 0;

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'nearest',
      },
      elements: {
        point: {
          radius: 0,
        },
      },
      scales: {
        y: {
          display: false,
          min: 0,
          max: Math.max(expectedStrength * 1.5, 20),
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
        x: {
          type: 'linear',
          position: 'bottom',
          min: xMin,
          max: xMax,
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
      },
      animation: {
        duration: 0,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            // tslint:disable-next-line: typedef
            label(context: any) {
              return `${Number(context.parsed.y).toFixed(1)} kg`;
            },
            // tslint:disable-next-line: typedef
            title(context: any) {
              return `Time: ${Number(context[0].parsed.x).toFixed(2)}s`;
            },
          },
          titleColor: '#0066ff',
          titleFont: { size: 16 },
          bodyColor: '#fff',
          bodyFont: { size: 14 },
          displayColors: false,
        },
      },
    };
  }

  render() {
    const dataArray = this.props.chartDataArray || [];
    const repTime = this.props.currentRep?.time || 10;
    const activityStatus = this.props.activityStatus;

    // Compute rolling window — shared by both chart data and chart options.
    // chartMaxTime is the true max x tracked by the reducer (immune to stale-props race).
    // During 'wait release' the window rolls right so old data gradually leaves the left edge.
    // All data is still passed to the chart (no filter); Chart.js clips at xMin naturally.
    const latestX = Math.max(this.props.chartMaxTime, dataArray.length > 0 ? dataArray[dataArray.length - 1].x : 0);
    const xMax = activityStatus === 'wait release' ? Math.max(repTime, latestX) : repTime;
    const xMin = activityStatus === 'wait release' ? Math.max(0, xMax - repTime) : 0;

    const chartData = this.generateChartData(xMin, xMax);
    const chartOptions = this.generateChartOptions(xMin, xMax);

    return (
      <Container style={{
        position: 'relative', height: '100%', width: '100%', margin: '10px 10px', borderRadius: '10px', backgroundColor: 'var(--color-chart-bg)', border: '1px solid var(--color-border)', overflow: 'visible',
      }}>
        <Line data={chartData as any} options={chartOptions} />
      </Container>
    );
  }
}

export default connect(mapStateToProps)(LiveGraph);
